import { useState } from 'react';
import { Alert, Button, Card, Group, Skeleton, Stack, Text } from '@mantine/core';
import { IconAlertCircle, IconPalette, IconSparkles } from '@tabler/icons-react';
import {
  BRAND_KIT_COPY as K,
  TV_BRAND_KIT_COPY,
  TV_PLATFORM_KEYS,
  type FactoryAssetPlatform,
  type TvPlatformKey,
} from '@/lib/constants/appFactory';
import { BrandKitUtils } from '../BrandKitUtils';
import { useFactoryAssetSets, useIsFactoryOperator } from '../useAppFactory';
import { useAssetSetDecisions } from '../useAssetSetDecisions';
import { AppCurrentImages } from './AppCurrentImages';
import { AppManualSet } from './AppManualSet';
import { AssetSetDraftCard } from './AssetSetDraftCard';
import { AssetSetHistory } from './AssetSetHistory';

interface AppImagesPanelProps {
  appCfgId: number;
  /** Whose deliverable set this panel manages (roku, firetv). */
  platform: FactoryAssetPlatform;
  disabled?: boolean;
  /** The TV platforms that are on for the app: screens and names follow them. Absent = all. */
  tv?: readonly TvPlatformKey[];
  /** Opens the app's Brand Kit tab — where these images are made. Absent = no button. */
  onOpenBrandKit?: () => void;
}

const noticeKey = (appCfgId: number) => `tvTilesNoticeDismissed:${appCfgId}`;
function readDismissed(appCfgId: number): boolean {
  try {
    return window.localStorage.getItem(noticeKey(appCfgId)) === '1';
  } catch {
    return false;
  }
}

/**
 * One platform's Images surface. Since the app-level Brand Kit (3.42.0) it has
 * NO generate form: the brand inputs live once on the app's Brand Kit tab, and
 * this panel keeps what is the platform's own — its current images and the TV
 * screens that show them, a pointer to the Brand Kit, the review of a draft
 * that did not come from the kit (an upload, or a set generated before the
 * kit), the per-file uploads and the history. A draft rendered from the kit
 * is reviewed on the Brand Kit tab with the other platforms' — one approval.
 * Every card reads the factory gateway, so an unreachable gateway must say so.
 */
export function AppImagesPanel({ appCfgId, platform, disabled, tv = TV_PLATFORM_KEYS, onOpenBrandKit }: AppImagesPanelProps) {
  const assetSetsQuery = useFactoryAssetSets(appCfgId, platform);
  const decisions = useAssetSetDecisions(appCfgId, tv);
  const isOperator = useIsFactoryOperator();
  const [dismissed, setDismissed] = useState(() => readDismissed(appCfgId));
  const dismiss = () => {
    setDismissed(true);
    try {
      window.localStorage.setItem(noticeKey(appCfgId), '1');
    } catch {
      /* private mode: the notice stays dismissed for this visit only */
    }
  };

  if (assetSetsQuery.isError) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} color="orange">
        Image management is temporarily unavailable. Your app and its current images are
        unaffected — try again in a few minutes, or contact support if this persists.
      </Alert>
    );
  }
  if (assetSetsQuery.isLoading) {
    return (
      <Stack gap="lg">
        <Skeleton height={120} radius="md" />
        <Skeleton height={220} radius="md" />
      </Stack>
    );
  }

  const assetSets = assetSetsQuery.data?.assetSets ?? [];
  const currentImages = assetSetsQuery.data?.currentImages ?? [];
  const pending = BrandKitUtils.pendingDraft(assetSets);
  const kitDraft = pending?.brandKitId ? pending : undefined;
  const ownDraft = pending && !pending.brandKitId ? pending : undefined;

  // TV graphics v2 (design D6): a live set from before composer v2 gets one
  // dismissible nudge — generating again never changes anything until approved.
  const showTvNotice = !dismissed && BrandKitUtils.needsTvTilesNotice(assetSets, currentImages, platform);

  const openButton = (label: string, variant: 'filled' | 'light' | 'subtle' = 'light') =>
    onOpenBrandKit ? (
      <Button size="xs" variant={variant} onClick={onOpenBrandKit} data-testid="open-brand-kit">
        {label}
      </Button>
    ) : null;

  return (
    <Stack gap="lg">
      {showTvNotice && (
        <Alert icon={<IconSparkles size={16} />} color="blue" withCloseButton onClose={dismiss} data-testid="tv-tiles-notice">
          <Group gap="sm">
            <Text size="sm">{TV_BRAND_KIT_COPY.v1Notice}</Text>
            {openButton(K.pointerOpen)}
          </Group>
        </Alert>
      )}

      {kitDraft && (
        <Alert icon={<IconPalette size={16} />} color="orange" data-testid="kit-draft-pointer">
          <Group gap="sm">
            <Text size="sm">{K.pointerDraft}</Text>
            {openButton(K.pointerReview, 'filled')}
          </Group>
        </Alert>
      )}

      <AppCurrentImages appCfgId={appCfgId} platform={platform} tv={tv} />

      {!kitDraft && (
        <Card withBorder radius="md" p="md" data-testid="brand-kit-pointer">
          <Group gap="md" justify="space-between" wrap="nowrap">
            <Group gap="xs" wrap="nowrap" align="flex-start">
              <IconPalette size={20} style={{ flex: 'none' }} />
              <Stack gap={2}>
                <Text fw={600} size="md">
                  {K.pointerTitle}
                </Text>
                <Text size="sm" c="dimmed">
                  {currentImages.length === 0 ? K.pointerEmpty : K.pointerLive}
                </Text>
              </Stack>
            </Group>
            {openButton(K.pointerOpen, currentImages.length === 0 ? 'filled' : 'light')}
          </Group>
        </Card>
      )}

      {ownDraft && (
        <Card withBorder radius="md" p="md">
          <Stack gap="md">
            <Text fw={600} size="md">
              {K.manualDraftTitle}
            </Text>
            <AssetSetDraftCard
              set={ownDraft}
              scope={platform}
              tv={tv}
              assetSets={assetSets}
              disabled={disabled}
              busy={decisions.busy}
              isOperator={isOperator}
              onApprove={() => decisions.approve({ scope: platform, set: ownDraft })}
              onDiscard={() => decisions.discard({ scope: platform, set: ownDraft })}
              onOverride={(_, reason) => decisions.override({ scope: platform, set: ownDraft }, reason)}
            />
          </Stack>
        </Card>
      )}

      <AppManualSet appCfgId={appCfgId} platform={platform} disabled={disabled} tv={tv} />

      {BrandKitUtils.historyRows(assetSets).length > 0 && (
        <Card withBorder radius="md" p="md">
          <AssetSetHistory assetSets={assetSets} showOverrideActor={isOperator} />
        </Card>
      )}
    </Stack>
  );
}
